/**
 * Parse the device's live `mdmDetails(GET_DEVICE_INFO)` reply into the fields the state cards show —
 * vendored from scio3 `features/mdm-devices/utils/parse-device-info.ts`, incl. the `settings` A/V
 * readback the Settings tab seeds Mute / Volume / Brightness from. Handles the scio3 backend shape and the scio1/Esper
 * shape; swallow-and-return-null on anything unusable.
 *
 * Delta: staff-api JSON-stringifies open-api's `mdmDetails`, which is itself a JSON string, so the
 * payload can arrive double-encoded — unwrapped here.
 */
/** Which layer a reported A/V value is coming from. */
export type AvSource = 'ops' | 'device';

/**
 * The device's ACTUAL A/V state read back from the hardware (SSSP-only today). Platforms that don't
 * report it leave this undefined and the Settings tab falls back to the stored values.
 */
export interface DeviceLiveSettings {
  /** Actual system volume 0–100. */
  volume?: number;
  muted?: boolean;
  /** Actual panel backlight 0–100. */
  backlight?: number;
  /** Per field: is an ops-schedule window ('ops') or the device level ('device') applying it now. */
  appliedSource?: { volume?: AvSource; brightness?: AvSource; mute?: AvSource };
}

export interface DeviceLiveInfo {
  serialNumber?: string;
  wifiSSID?: string;
  displayWidth?: string;
  displayHeight?: string;
  refreshRate?: string;
  ramUsage?: string;
  ramTotal?: string;
  storageUsage?: string;
  storageTotal?: string;
  temperature?: string;
  uptimeSeconds?: number;
  batteryPercent?: number;
  settings?: DeviceLiveSettings;
}

/** MB → "1.23 GB". */
function toGB(value: number | string | undefined): string | undefined {
  if (value == null) return undefined;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num) || num <= 0) return undefined;
  return `${(num / 1024).toFixed(2)} GB`;
}

function toPositive(value: unknown): number | undefined {
  if (value == null) return undefined;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (typeof num !== 'number' || Number.isNaN(num) || num <= 0) return undefined;
  return num;
}

function toCelsius(value: unknown): string | undefined {
  const n = toPositive(value);
  return n == null ? undefined : `${Math.round(n)}°C`;
}

function toBatteryPercent(value: unknown): number | undefined {
  if (value == null) return undefined;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (typeof num !== 'number' || Number.isNaN(num) || num < 0 || num > 100) return undefined;
  return Math.round(num);
}

function parseAppliedSource(raw: unknown): DeviceLiveSettings['appliedSource'] {
  if (!raw || typeof raw !== 'object') return undefined;
  const s = raw as { volume?: unknown; brightness?: unknown; mute?: unknown };
  const src = (v: unknown): AvSource | undefined => (v === 'ops' || v === 'device' ? v : undefined);
  const out = { volume: src(s.volume), brightness: src(s.brightness), mute: src(s.mute) };
  return Object.values(out).some((v) => v != null) ? out : undefined;
}

/** Narrow a raw `settings` blob; each field type-guarded and omitted individually. */
function parseSettings(raw: unknown): DeviceLiveSettings | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const s = raw as {
    audio?: { volume?: unknown; muted?: unknown };
    backlight?: unknown;
    appliedSource?: unknown;
  };
  const parsed: DeviceLiveSettings = {
    volume: typeof s.audio?.volume === 'number' ? s.audio.volume : undefined,
    muted: typeof s.audio?.muted === 'boolean' ? s.audio.muted : undefined,
    backlight: typeof s.backlight === 'number' ? s.backlight : undefined,
    appliedSource: parseAppliedSource(s.appliedSource),
  };
  return Object.values(parsed).some((v) => v != null) ? parsed : undefined;
}

/** Parse up to two JSON layers until an object comes out. */
function unwrap(raw: unknown): Record<string, unknown> | null {
  let v: unknown = raw;
  for (let i = 0; i < 3 && typeof v === 'string'; i += 1) {
    if (!v) return null;
    v = JSON.parse(v);
  }
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
}

export function parseDeviceLiveInfo(raw: unknown): DeviceLiveInfo | null {
  if (!raw) return null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped device blob
    const data = unwrap(raw) as any;
    if (!data) return null;
    const display = Array.isArray(data?.displays) ? data.displays[0] : data?.displays;
    const wifiSSID = data?.networkInfo?.wifiSSID || data?.wifi_access_points?.[0]?.wifi_ssid;

    const totalRam = data?.memoryInfo?.totalRam;
    const freeRam = data?.memoryInfo?.freeRam;
    const totalStorage = data?.memoryInfo?.totalInternalStorage;
    const freeStorage = data?.memoryInfo?.freeStorage;
    const ramUsage = totalRam != null && freeRam != null ? totalRam - freeRam : undefined;
    const storageUsage =
      totalStorage != null && freeStorage != null ? totalStorage - freeStorage : undefined;

    const parsed: DeviceLiveInfo = {
      serialNumber: data?.hardwareInfo?.serialNumber,
      wifiSSID,
      displayWidth: display?.width != null ? String(display.width) : undefined,
      displayHeight: display?.height != null ? String(display.height) : undefined,
      refreshRate: display?.refreshRate != null ? String(display.refreshRate) : undefined,
      ramUsage: toGB(ramUsage),
      ramTotal: toGB(totalRam),
      storageUsage: toGB(storageUsage),
      storageTotal: toGB(totalStorage),
      temperature: toCelsius(
        data?.thermalInfo?.cpuTemp ?? data?.thermalInfo?.temperature ?? data?.cpuTemp,
      ),
      uptimeSeconds: toPositive(data?.uptimeSeconds),
      batteryPercent: toBatteryPercent(data?.batteryInfo?.percentage ?? data?.batteryLevel),
      settings: parseSettings(data?.settings),
    };
    const hasAny = Object.values(parsed).some((v) => v != null && v !== '');
    return hasAny ? parsed : null;
  } catch {
    return null;
  }
}
